import { Thumb } from "../lib/thumb";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDateRange } from "@/lib/dateRange";
import { useChannel, channelParam } from "@/lib/channel";
import { fmtNumber, fmtDateShort } from "@/lib/format";
import { useCurrency } from "@/lib/currency";
import KpiCard from "@/components/KpiCard";
import MobileKpiRail from "@/components/MobileKpiRail";
import Card from "@/components/Card";
import PageHeader from "@/components/PageHeader";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
  BarChart, Bar, Cell, PieChart, Pie,
} from "recharts";
import {
  DollarSign, ShoppingBag, Target, TrendingUp, Package, TrendingDown, ArrowRight,
  Download, Printer,
} from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";

const CHART_PURPLE = "#A87552";
const CHART_ORANGE = "#C8915A";
const CHART_GREEN = "#7D9B7A";
const CHART_PINK = "#D8AAA2";
const CHART_TEAL = "#89AEB3";

const PIE_COLORS = [CHART_PURPLE, CHART_ORANGE, CHART_GREEN, CHART_PINK, CHART_TEAL, "#C5A56A", "#B75B50"];

function ChartTooltip({ active, payload, label }) {
  const { fmtMoney } = useCurrency();
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.10)] border border-black/[0.06] px-3 py-2.5 text-[12px]">
      {label && <div className="text-[#8E8E93] mb-1.5 text-[11px]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#3C3C43]/80 font-medium">{p.name}:</span>
          <span className="font-mono text-[#1C1C1E] font-semibold">{fmtMoney(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// ------- (kept for compat; not used) ---------
function _LegacyProgress() { return null; }


// ------- Invoice-style progress list with thumbnail ---------
function InvoiceProgress({ label, value, max, color, testid, image }) {
  const { fmtMoney } = useCurrency();
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0" data-testid={testid}>
      <div className="flex items-center gap-3">
        {image ? (
          <Thumb src={image} alt={label} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-black/5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center text-[13px] mb-1">
            <span className="text-[#1C1C1E] font-medium truncate pr-2">{label}</span>
            <span className="font-body font-medium tabular-nums tracking-[-0.01em] text-[12.5px] text-[#3F342D] flex-shrink-0">{fmtMoney(value)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#F5F5F7] overflow-hidden">
            <div className="h-full rounded-full progress-bar" style={{ width: `${pct}%`, background: color }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { range } = useDateRange();
  const { channel } = useChannel();
  const { fmtMoney, displayCurrency, CURRENCIES } = useCurrency();

  // Compact axis formatter: converts USD value → display currency, abbreviated
  const fmtAxis = (v) => {
    const cur = CURRENCIES.find((c) => c.code === displayCurrency) || CURRENCIES[0];
    const converted = v * cur.rate;
    const sym = cur.symbol;
    if (Math.abs(converted) >= 1000000) return `${sym}${(converted / 1000000).toFixed(1)}M`;
    if (Math.abs(converted) >= 1000) return `${sym}${(converted / 1000).toFixed(0)}k`;
    return `${sym}${converted.toFixed(0)}`;
  };
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [costWarnDismissed, setCostWarnDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    setKpis(null);
    setSeries([]);
    setTop([]);
    setBreakdown([]);
    const params = { start: range.start, end: range.end, channel: channelParam(channel) };
    api.get("/analytics/dashboard", { params }).then((dashboard) => {
      if (!alive) return;
      setKpis(dashboard.data.kpis);
      setSeries(dashboard.data.revenue_series?.series || []);
      setTop(dashboard.data.top_products || []);
      setBreakdown(dashboard.data.expense_breakdown || []);
    }).catch(() => {
      if (alive) setLoadError(true);
    }).finally(() => {
      if (alive) setLoading(false);
    });

    api.get("/products").then((p) => {
      if (!alive) return;
      setAllProducts(p.data || []);
      setLowStock((p.data || []).filter((x) => x.stock <= (x.reorder_point || 10)).slice(0, 5));
    }).catch(() => {
      // Inventory warnings are supplementary and must not block the dashboard.
    });
    return () => { alive = false; };
  }, [range.start, range.end, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalExp = breakdown.reduce((s, x) => s + x.amount, 0);
  const maxTopRev = top.length > 0 ? Math.max(...top.map((t) => t.revenue)) : 1;
  const missingCostCount = allProducts.filter((p) => p.price > 0 && (!p.cost || p.cost === 0)).length;
  const hasRevenueSeriesData = series.some(
    (point) => Number(point.revenue) !== 0 || Number(point.previous_revenue) !== 0
  );

  return (
    <div data-testid="dashboard-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        greeting={greetingByHour() + "."}
        title="Here's your studio today."
        subtitle="A snapshot across bookings, services, retail and profit."
        testid="dashboard-header"
      />

      {/* KPI grid */}
      <MobileKpiRail className="mb-4 sm:mb-6">
        <KpiCard
          testid="kpi-revenue"
          label="Revenue"
          value={fmtMoney(kpis?.revenue)}
          change={kpis?.revenue_change}
          sub="Net of tax & shipping"
          icon={DollarSign}
          color={CHART_PURPLE}
          sparkline={series}
          sparkKey="revenue"
          loading={loading}
        />
        <KpiCard
          testid="kpi-orders"
          label="Sales"
          value={fmtNumber(kpis?.orders)}
          change={kpis?.orders_change}
          sub={kpis?.refunds ? `${fmtMoney(kpis.refunds)} refunded` : "no refunds"}
          icon={ShoppingBag}
          color={CHART_ORANGE}
          sparkline={series}
          sparkKey="orders"
          loading={loading}
        />
        <KpiCard
          testid="kpi-aov"
          label="Avg ticket"
          value={fmtMoney(kpis?.aov)}
          change={kpis?.aov_change}
          sub={kpis ? (kpis.gross_margin == null ? "Margin —" : `Margin ${kpis.gross_margin}%`) : "Margin —"}
          icon={Target}
          color={CHART_PINK}
          sparkline={series}
          sparkKey="revenue"
          loading={loading}
        />
        <KpiCard
          testid="kpi-net-profit"
          label="Net profit"
          value={fmtMoney(kpis?.net_profit)}
          change={kpis?.net_profit_change}
          sub={`Expenses ${fmtMoney(kpis?.expenses)}`}
          icon={TrendingUp}
          color={CHART_GREEN}
          sparkline={series}
          sparkKey="profit"
          loading={loading}
        />
      </MobileKpiRail>

      {/* Missing cost warning */}
      {missingCostCount > 0 && !costWarnDismissed && (
        <div
          data-testid="missing-cost-warning"
          className="mb-4 sm:mb-6 flex items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex items-start gap-3 min-w-0">
            <TrendingDown className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[13px] text-amber-800 leading-snug">
              <span className="font-semibold">{missingCostCount} product{missingCostCount !== 1 ? "s" : ""} {missingCostCount !== 1 ? "have" : "has"} no cost set.</span>{" "}
              Gross and net profit figures may be understated.{" "}
              <Link to="/products" className="underline font-medium hover:text-amber-900">
                Set costs on the Products page
              </Link>{" "}
              to fix this.
            </p>
          </div>
          <button
            aria-label="Dismiss warning"
            onClick={() => setCostWarnDismissed(true)}
            className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors text-[18px] leading-none mt-0.5"
          >
            ×
          </button>
        </div>
      )}

      {/* Revenue chart + P&L */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <Card
          testid="revenue-chart-card"
          className="lg:col-span-2"
          title="Revenue over time"
          subtitle="Solid — this period · Dotted — previous"
        >
          {loading ? (
            <div className="h-[260px] sm:h-[300px] rounded-2xl bg-black/[0.025] animate-pulse" />
          ) : loadError ? (
            <div className="h-[260px] sm:h-[300px] flex items-center justify-center text-[13px] text-[#B01C1C]">
              Dashboard data could not be loaded.
            </div>
          ) : hasRevenueSeriesData ? (
          <div className="chart-container mobile-chart h-[260px] sm:h-[300px] -ml-2">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 260 }}>
              <AreaChart data={series} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_PURPLE} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={CHART_PURPLE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(0,0,0,0.04)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDateShort}
                  tick={{ fill: "#8E8E93", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#8E8E93", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmtAxis}
                  width={52}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="previous_revenue"
                  name="Previous"
                  stroke="rgba(0,0,0,0.22)"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  fill="transparent"
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke={CHART_PURPLE}
                  strokeWidth={2.4}
                  fill="url(#rev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          ) : (
            <div className="h-[260px] sm:h-[300px] flex items-center justify-center text-[13px] text-[#8E8E93]">
              No revenue recorded for this or the previous period.
            </div>
          )}
        </Card>

        <Card testid="pl-card" title="Profit & Loss" subtitle="Snapshot for the period" action={
          <div className="flex items-center gap-1">
            <a
              data-testid="export-csv-btn"
               href={`${API}/export/pl.csv?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}${channelParam(channel) ? `&channel=${encodeURIComponent(channelParam(channel))}` : ""}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11.5px] text-[#3C3C43]/80 hover:text-[#1C1C1E] inline-flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 bg-white transition-colors"
              title="Download CSV"
            >
              <Download className="w-3 h-3" /> CSV
            </a>
            <button
              data-testid="export-pdf-btn"
              onClick={() => window.print()}
              className="text-[11.5px] text-[#3C3C43]/80 hover:text-[#1C1C1E] inline-flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 bg-white transition-colors"
              title="Print / Save as PDF"
            >
              <Printer className="w-3 h-3" /> PDF
            </button>
          </div>
        }>
          <div className="space-y-1">
            {[
              ["Gross sales", kpis?.gross_sales],
              ["– Discounts", -(kpis?.discounts ?? 0)],
              ["Net revenue", kpis?.revenue, true],
              ["– COGS", -(kpis?.cogs ?? 0)],
              ["Gross profit", kpis?.gross_profit, true],
              ["– Expenses", -(kpis?.expenses ?? 0)],
              ["Net profit", kpis?.net_profit, true],
            ].map(([label, val, bold], i) => (
              <div key={i} className={`flex items-center justify-between text-[13px] py-1.5 ${bold ? "border-t border-black/[0.05] pt-3 mt-1 font-semibold text-[#1C1C1E]" : ""}`}>
                <span className={bold ? "text-[#1C1C1E]" : "text-[#3C3C43]/80"}>{label}</span>
                <span className={`font-mono ${val < 0 ? "text-[#FF3B30]" : "text-[#1C1C1E]"}`}>
                  {fmtMoney(val)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top products progress + Expense mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <Card
          testid="top-products-progress-card"
           title="Top revenue drivers"
           subtitle="Services and retail in period"
          className="lg:col-span-2"
          action={
               <Link to="/services" className="text-[12px] text-[#A9684E] hover:underline flex items-center gap-1 font-medium">
               View services <ArrowRight className="w-3 h-3" />
            </Link>
          }
        >
          {loading ? (
            <div className="h-32 rounded-2xl bg-black/[0.025] animate-pulse" />
          ) : loadError ? (
            <div className="text-[13px] text-[#B01C1C] py-6 text-center">Revenue drivers could not be loaded.</div>
          ) : top.length === 0 ? (
            <div className="text-[13px] text-[#8E8E93] py-6 text-center">No sales in this period.</div>
          ) : (
            top.map((t, i) => {
              const prod = allProducts.find((p) => p.sku === t.sku);
              return (
                <InvoiceProgress
                  key={t.sku || i}
                  testid={`top-product-${t.sku || i}`}
                  label={t.name}
                  value={t.revenue}
                  max={maxTopRev}
                  color={PIE_COLORS[i % PIE_COLORS.length]}
                  image={prod?.image_url}
                />
              );
            })
          )}
        </Card>

        <Card testid="expense-breakdown-card" title="Expense mix" subtitle={`Total ${fmtMoney(totalExp)}`}>
          {loading ? (
            <div className="h-48 rounded-2xl bg-black/[0.025] animate-pulse" />
          ) : loadError ? (
            <div className="text-[13px] text-[#B01C1C] py-6 text-center">Expense data could not be loaded.</div>
          ) : breakdown.length === 0 ? (
            <div className="text-[13px] text-[#8E8E93] py-6 text-center">No expenses recorded.</div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="chart-container h-[196px] w-full relative">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 280, height: 196 }}>
                  <PieChart>
                    <Pie
                      data={breakdown}
                      dataKey="amount"
                      nameKey="category"
                      innerRadius={66}
                      outerRadius={84}
                      paddingAngle={3}
                      cornerRadius={4}
                    >
                      {breakdown.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-3">
                  <div className="text-[9.5px] uppercase tracking-[0.16em] text-[#8E8E93]">Total</div>
                  <div className="font-body font-medium tabular-nums tracking-[-0.025em] text-[15px] mt-1 whitespace-nowrap max-w-[116px] text-center">
                    {fmtMoney(totalExp)}
                  </div>
                </div>
              </div>
              <div className="w-full space-y-1.5 mt-3">
                {breakdown.slice(0, 5).map((b, i) => (
                  <div key={b.category} className="flex items-center justify-between text-[12px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-[#3C3C43]/80 truncate">{b.category}</span>
                    </div>
                    <span className="font-mono font-medium">{fmtMoney(b.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

       {/* Revenue by offering bar chart */}
       <Card testid="top-products-card" title="Revenue by offering" subtitle="Comparative view" className="mb-4 sm:mb-6">
         {loading ? (
          <div className="h-[240px] rounded-2xl bg-black/[0.025] animate-pulse" />
        ) : loadError ? (
          <div className="h-[180px] flex items-center justify-center text-[13px] text-[#B01C1C]">
            Offering revenue could not be loaded.
          </div>
        ) : top.length > 0 ? (
        <div className="chart-container mobile-chart h-[240px] -ml-2">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 240 }}>
            <BarChart data={top} margin={{ top: 10, right: 8, bottom: 0, left: 0 }} barCategoryGap="30%">
              <CartesianGrid stroke="rgba(0,0,0,0.04)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#8E8E93", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={-12}
                textAnchor="end"
                height={60}
                tickFormatter={(v) => v?.length > 12 ? v.slice(0, 12) + "…" : v}
              />
              <YAxis
                tick={{ fill: "#8E8E93", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmtAxis}
                width={52}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="revenue" name="Revenue" radius={[14, 14, 4, 4]} maxBarSize={64}>
                {top.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        ) : (
          <div className="h-[180px] flex items-center justify-center text-[13px] text-[#8E8E93]">
            No offering revenue recorded for this period.
          </div>
        )}
      </Card>

      {/* Low stock */}
      <Card
        testid="low-stock-card"
        title="Low stock alerts"
        subtitle="At or below reorder point"
        action={
          <Link to="/inventory" className="text-[12px] text-[#A9684E] hover:underline flex items-center gap-1 font-medium">
            Inventory <ArrowRight className="w-3 h-3" />
          </Link>
        }
      >
        {lowStock.length === 0 ? (
          <div className="text-[13px] text-[#8E8E93] flex items-center gap-2 py-4 justify-center">
             <Package className="w-4 h-4" /> All retail products are well-stocked.
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {lowStock.map((p) => (
              <div key={p.id} data-testid={`low-stock-${p.sku}`} className="flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-[#FAFAFA] border border-black/[0.05] hover:bg-white transition-colors">
                {p.image_url ? (
                  <Thumb src={p.image_url} alt={p.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center flex-shrink-0"><Package className="w-4 h-4 text-[#8E8E93]" /></div>
                )}
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium truncate max-w-[160px]">{p.name}</div>
                  <div className="text-[11px] text-[#FF3B30] font-semibold">● {p.stock} left</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
